import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getKpiTypeColor } from "../../../utils/kpiColors";

const KPIRecurringList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { upcomingRecurring, upcomingRecurringCounts, dashboard } = useSelector((state) => state.kpi);

  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [deleteModalWork, setDeleteModalWork] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUpcomingRecurringList = React.useCallback((tab = activeTab) => {
    const params = {};
    if (tab === "active") {
      params.status = "ACTIVE";
    } else if (tab === "paused") {
      params.status = "PAUSED";
    } else if (tab === "cancelled") {
      params.status = "CANCELLED";
    } else {
      params.status = "ALL";
    }
    dispatch.kpi.getUpcomingRecurring(params);
  }, [dispatch, activeTab]);

  useEffect(() => {
    fetchUpcomingRecurringList(activeTab);
  }, [fetchUpcomingRecurringList, activeTab]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.recurring-action-menu-container')) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const recurringList = (Array.isArray(upcomingRecurring) && upcomingRecurring.length > 0)
    ? upcomingRecurring
    : (dashboard?.upcoming_recurring || dashboard?.upcomingRecurring || []);

  const activeCount = upcomingRecurringCounts?.active ?? 0;
  const pausedCount = upcomingRecurringCounts?.paused ?? 0;
  const cancelledCount = upcomingRecurringCounts?.cancelled ?? 0;
  const allCount = (upcomingRecurringCounts?.all !== undefined && upcomingRecurringCounts?.all !== null)
    ? upcomingRecurringCounts.all
    : (activeCount + pausedCount + cancelledCount);

  const baseTabs = [
    { id: "all", label: "All Schedules", count: allCount },
    { id: "active", label: "Active", count: activeCount },
    { id: "paused", label: "Paused", count: pausedCount },
    { id: "cancelled", label: "Cancelled", count: cancelledCount }
  ];

  const getStatusString = (row) => {
    if (!row) return "ACTIVE";
    const st = row.status || row.recurrence_status || (row.is_active === false ? "PAUSED" : "ACTIVE");
    return String(st).toUpperCase().trim();
  };

  const getRecurrenceId = (row) => {
    if (!row) return null;
    return row.recurrence_id || row._id || row.id || row.task_id;
  };

  const filteredList = recurringList.filter((row) => {
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const title = (row.task_name || row.title || "").toLowerCase();
      const proj = (row.project_name || row.project?.name || "").toLowerCase();
      const recId = (getRecurrenceId(row) || "").toLowerCase();
      return title.includes(q) || proj.includes(q) || recId.includes(q);
    }
    return true;
  });

  const handleStatusChange = async (row, newStatus) => {
    const recId = getRecurrenceId(row);
    if (!recId) return;
    setIsSubmitting(true);
    try {
      await dispatch.kpi.updateRecurringStatus({ id: recId, status: newStatus });
      fetchUpcomingRecurringList(activeTab);
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCancelSchedule = async () => {
    if (!deleteModalWork) return;
    const recId = getRecurrenceId(deleteModalWork);
    if (!recId) return;

    setIsSubmitting(true);
    try {
      await dispatch.kpi.updateRecurringStatus({ id: recId, status: "CANCELLED" });
      fetchUpcomingRecurringList(activeTab);
      setDeleteModalWork(null);
    } catch (err) {
      console.error("Failed to cancel recurring schedule", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Recurring Schedules</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Automated recurring task templates and generation schedules
          </p>
        </div>

        <Link
          to="/kpi/create"
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors shadow-xs !no-underline"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Recurring Work
        </Link>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {baseTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative w-full md:w-80">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search recurring schedules..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-full text-xs text-slate-900 bg-white shadow-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
          />
        </div>
      </div>

      {/* Main Recurring List Table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs">
        <div className="overflow-x-auto rounded-xl">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-200/80 bg-[#f7f6f3]">
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-24">ID</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">TITLE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-36">TYPE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-32">CADENCE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-36">STATUS</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-20">KPI</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-24">OWNER</th>
                <th className="px-6 py-3.5 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-sm font-semibold text-slate-500">
                    No recurring schedules found for this filter.
                  </td>
                </tr>
              ) : (
                filteredList.map((row, idx) => {
                  const recId = getRecurrenceId(row) || `REC-${idx}`;
                  const displayWorkId = row.work_id || row.human_readable_id || (row._id ? `REC-${row._id.slice(-6)}` : recId);
                  const title = row.task_name || row.title || row.name || "Untitled Schedule";

                  const typeObj = row.kpi_type || row.task_type;
                  const typeName = typeObj?.name || row.type || "Task";
                  const typeColor = getKpiTypeColor(typeName);

                  const statusStr = getStatusString(row);
                  const isRowCancelled = statusStr === "CANCELLED";
                  const isRowPaused = statusStr === "PAUSED";
                  const isRowActive = statusStr === "ACTIVE";

                  const frequency = row.frequency || row.recurrence_frequency || row.cadence || "WEEKLY";

                  const ptsVal = row.assigned_kpi_points !== undefined ? row.assigned_kpi_points : (row.kpi_type?.score || 20);

                  const ownerUser = row.created_by || row.owner || row.assigned_by || row.creator;
                  const getOwnerFullName = (u) => {
                    if (!u) return "N/A";
                    if (typeof u === 'string') return u;
                    if (u.full_name) return u.full_name;
                    if (u.first_name || u.last_name) return `${u.first_name || ''} ${u.last_name || ''}`.trim();
                    return u.name || u.email || "N/A";
                  };
                  const ownerName = getOwnerFullName(ownerUser);
                  const getOwnerInitials = (u) => {
                    if (!u) return "?";
                    const name = getOwnerFullName(u);
                    if (!name || name === "N/A") return "?";
                    const parts = name.trim().split(" ");
                    if (parts.length >= 2) {
                      return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
                    }
                    return name.substring(0, 2).toUpperCase();
                  };
                  const ownerInitials = getOwnerInitials(ownerUser);
                  const getAvatarBgColor = (initialsStr) => {
                    if (!initialsStr) return "bg-[#147b56]";
                    const charCode = (initialsStr.charCodeAt(0) || 0) + (initialsStr.charCodeAt(1) || 0);
                    const colors = ["bg-[#ab6919]", "bg-[#1d5bbd]", "bg-[#147b56]", "bg-[#6558e8]", "bg-[#c73838]"];
                    return colors[charCode % colors.length];
                  };

                  return (
                    <tr
                      key={recId || idx}
                      className="hover:bg-slate-50/70 transition-colors group cursor-pointer"
                      onClick={() => navigate(`/kpi/recurring/${recId}`)}
                    >
                      {/* ID */}
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold text-slate-500 font-mono">
                          {displayWorkId}
                        </span>
                      </td>

                      {/* TITLE */}
                      <td className="px-6 py-4">
                        <div className="block max-w-xs">
                          <div className="text-sm font-bold text-slate-900 line-clamp-1">
                            {title}
                          </div>
                        </div>
                      </td>

                      {/* TYPE */}
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${typeColor.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${typeColor.bg}`}></span>
                          <span>{typeName}</span>
                        </div>
                      </td>

                      {/* CADENCE */}
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold text-slate-700">
                          {frequency ? (frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase()) : ""}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4">
                        {isRowActive && (
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                            <span>Active</span>
                          </div>
                        )}
                        {isRowPaused && (
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                            <span>Paused</span>
                          </div>
                        )}
                        {isRowCancelled && (
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                            <span>Cancelled</span>
                          </div>
                        )}
                      </td>

                      {/* KPI */}
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-slate-900">+{ptsVal}</span>
                      </td>

                      {/* OWNER */}
                      <td className="px-6 py-4">
                        {ownerUser ? (
                          <div className="flex items-center gap-2" title={ownerName}>
                            <div className={`w-7 h-7 rounded-full ${getAvatarBgColor(ownerInitials)} text-white font-bold text-[10px] flex items-center justify-center shadow-2xs shrink-0`}>
                              {ownerInitials}
                            </div>
                            <span className="text-xs font-semibold text-slate-700 truncate max-w-[110px]">
                              {ownerName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">N/A</span>
                        )}
                      </td>

                      {/* ACTION MENU */}
                      <td className="px-6 py-4 text-right relative">
                        <div className="relative inline-block text-left recurring-action-menu-container">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuId(activeMenuId === recId ? null : recId);
                            }}
                            className="text-slate-500 hover:text-slate-900 transition-colors p-1.5 rounded-lg hover:bg-slate-100 focus:outline-none"
                            title="Actions"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="5" r="1.2"></circle>
                              <circle cx="12" cy="12" r="1.2"></circle>
                              <circle cx="12" cy="19" r="1.2"></circle>
                            </svg>
                          </button>

                          {activeMenuId === recId && (
                            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-white rounded-xl shadow-lg border border-slate-200 p-1 z-50 flex items-center gap-1 whitespace-nowrap animate-in fade-in slide-in-from-right-2 duration-150">
                              {!isRowCancelled && (
                                <>
                                  {isRowActive ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenuId(null);
                                        handleStatusChange(row, "PAUSED");
                                      }}
                                      className="px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 rounded-lg flex items-center gap-1.5 transition-colors"
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                                      Pause
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenuId(null);
                                        handleStatusChange(row, "ACTIVE");
                                      }}
                                      className="px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded-lg flex items-center gap-1.5 transition-colors"
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                      Resume
                                    </button>
                                  )}

                                  <div className="w-[1px] h-4 bg-slate-200 my-auto"></div>
                                </>
                              )}

                              {!isRowCancelled ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuId(null);
                                    setDeleteModalWork(row);
                                  }}
                                  className="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg flex items-center gap-1.5 transition-colors"
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                  Cancel
                                </button>
                              ) : (
                                <span className="px-3 py-1.5 text-xs font-semibold text-slate-400 italic">Cancelled</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel Recurring Schedule Confirmation Modal */}
      {deleteModalWork && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Cancel Recurring Schedule</h3>
                <p className="text-xs text-rose-600 font-semibold">Terminal State Warning</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to cancel <span className="font-semibold text-slate-900">"{deleteModalWork.task_name || deleteModalWork.title}"</span>?
              <br/><br/>
              <span className="text-rose-600 font-bold block">Cancelled recurring schedules cannot be resumed or modified.</span>
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteModalWork(null)}
                disabled={isSubmitting}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
              >
                Keep Active
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelSchedule}
                disabled={isSubmitting}
                className="px-4 py-2 bg-rose-600 text-white rounded-xl text-xs font-semibold hover:bg-rose-700 transition-colors shadow-xs"
              >
                {isSubmitting ? "Cancelling..." : "Confirm Cancel Schedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIRecurringList;
